import React, { FunctionComponent, useState } from 'react'
import { useQuery } from 'urql'
import { REQUEST_POLICY } from 'constants/urql'
import { Button } from '@chakra-ui/react'
import { startCase } from 'lodash'
import {
  LeadGroupByUserIdDocument,
  useToursByLeadGroupIdQuery,
  ToursByLeadGroupIdQuery
} from 'graphql/generated'

import { Sidepeek } from 'ui'
import { formatDate } from 'lib/date-time'
import { ContentSectionCard } from 'components/TailwindUIToolkit'
import Error from 'components/Toolkit/Text/Error'
import * as SC from 'components/TailwindUIToolkit/StyledComponents'
import CreateTour from './CreateTourForm'

type ToursPropTypes = {
  userId: string
}

const ToursWrapper: FunctionComponent<React.PropsWithChildren<ToursPropTypes>> = ({ userId }) => {
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false)

  const [{ data: leadGroupData }] = useQuery({
    query: LeadGroupByUserIdDocument,
    variables: { id: userId },
    requestPolicy: REQUEST_POLICY.CACHE_AND_NETWORK,
    context: React.useMemo(() => ({ suspense: false }), [])
  })
  const leadGroupId = leadGroupData?.lead_group_users?.[0]?.lead_group?.id

  const [{ data: toursData }] = useToursByLeadGroupIdQuery({
    variables: {
      id: leadGroupId || ''
    },
    pause: !leadGroupId
  })
  const tours = toursData?.tours || []

  if (!leadGroupData || leadGroupData?.lead_group_users.length === 0 || !leadGroupId) {
    return <Error>Lead group not found for user id: {userId}</Error>
  }

  const createTourButton = (
    <Button size='xs' onClick={() => setDrawerOpen(true)}>
      + Tour
      <Sidepeek isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} title='Create Tour'>
        <CreateTour leadGroupId={leadGroupId} onClose={() => setDrawerOpen(false)} />
      </Sidepeek>
    </Button>
  )

  return (
    <ContentSectionCard
      title='Tours'
      size='large'
      padding={!tours.length}
      action={createTourButton}
    >
      <Tours tours={tours} />
    </ContentSectionCard>
  )
}

const Tours: FunctionComponent<
  React.PropsWithChildren<{ tours: ToursByLeadGroupIdQuery['tours'] }>
> = ({ tours }) => {
  if (!tours.length) {
    return <SC.MessageText>No tours found</SC.MessageText>
  }

  return (
    <div>
      {tours.map((tour) => {
        const details = [
          {
            icon: <SC.CalendarIcon />,
            text: formatDate(tour.start_at, 'MMM D, YYYY h:mm A')
          },
          {
            icon: <SC.LockOpenIcon />,
            text: startCase(tour.type || '')
          }
        ]

        const propertyAddress =
          `${tour.tour_properties?.[0]?.mls_listing?.display_line_1}, ${tour.tour_properties?.[0]?.mls_listing?.display_line_2}` ||
          'No address'

        return (
          <SC.PropertyBox key={tour.id}>
            <SC.SubsectionTitle>{propertyAddress}</SC.SubsectionTitle>

            {details.map((detail, i) =>
              detail.text ? (
                <SC.PropertyDetail key={i}>
                  {detail.icon}
                  {detail.text}
                </SC.PropertyDetail>
              ) : null
            )}
          </SC.PropertyBox>
        )
      })}
    </div>
  )
}

export default ToursWrapper
